export type Theme = 'light' | 'dark';
export type Role = 'user' | 'assistant' | 'error';
export type DiagramSortMode = 'recent' | 'oldest' | 'alpha';
export type MobilePane = 'projects' | 'compose' | 'canvas';
export type ComposerTab = 'chat' | 'code' | 'history';
export type CanvasMode = 'mermaid' | 'draw';
export type RevisionSource = 'seed' | 'chat' | 'editor' | 'restore' | 'autofix';

export type PipelineStage =
  | 'idle'
  | 'sending'
  | 'generating'
  | 'validating'
  | 'rendering'
  | 'completed'
  | 'cancelled'
  | 'error';

export type ChatMessage = {
  id: string;
  role: Role;
  text: string;
  createdAt: string;
};

export type Project = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
};

export type DiagramRevision = {
  id: string;
  mermaidCode: string;
  source: RevisionSource;
  note: string;
  createdAt: string;
};

export type ExcalidrawData = {
  elements: unknown[];
  appState: Record<string, unknown>;
  files: Record<string, unknown>;
};

export type DiagramType = 'mermaid' | 'excalidraw';

export type Diagram = {
  id: string;
  projectId: string;
  title: string;
  type: DiagramType;
  mermaidCode: string;
  excalidrawData?: ExcalidrawData;
  theme: Theme;
  pinned?: boolean;
  tags?: string[];
  chat: ChatMessage[];
  revisions: DiagramRevision[];
  revisionCursor: number;
  createdAt: string;
  updatedAt: string;
};

export type StudioState = {
  model: string;
  diagramSortMode: DiagramSortMode;
  projects: Project[];
  diagrams: Diagram[];
  selectedProjectId: string;
  selectedDiagramId: string;
};

export type PendingImage = {
  name: string;
  mediaType: string;
  base64: string;
  dataUrl: string;
};

export type MermaidErrorInfo = {
  raw: string;
  line?: number;
  column?: number;
};

export type LastRequest = {
  prompt: string;
  image: PendingImage | null;
  codeAtSubmit: string;
  excalidrawElements?: unknown[];
};

export type BootState = {
  store: StudioState;
  onboardingDone: boolean;
};

export const STAGE_FLOW: Array<'sending' | 'generating' | 'validating' | 'rendering'> = [
  'sending',
  'generating',
  'validating',
  'rendering',
];

export const STAGE_LABELS: Record<PipelineStage, string> = {
  idle: 'Ready',
  sending: 'Sending request',
  generating: 'Claude generating Mermaid',
  validating: 'Validating syntax',
  rendering: 'Rendering canvas',
  completed: 'Completed',
  cancelled: 'Cancelled',
  error: 'Failed',
};

export const STARTER_TEMPLATES = [
  {
    id: 'git-flow',
    label: 'Git Flow',
    prompt: 'Generate a Git Flow diagram for a team with feature, release, and hotfix branches.',
  },
  {
    id: 'auth',
    label: 'Auth Flow',
    prompt: 'Create an authentication flow with frontend, API gateway, auth service, and database.',
  },
  {
    id: 'cicd',
    label: 'CI/CD Pipeline',
    prompt: 'Design a CI/CD pipeline from commit to production with tests and approval gates.',
  },
  {
    id: 'architecture',
    label: 'Microservices',
    prompt: 'Create a microservices architecture with API gateway, service layer, Redis cache, and RabbitMQ.',
  },
  {
    id: 'erd',
    label: 'E-commerce ERD',
    prompt: 'Generate an ER diagram for users, orders, products, categories, and order_items.',
  },
];

export const DEFAULT_MODEL = 'claude-opus-4-6';
export const MAX_REVISIONS = 60;

export const STORE_KEY = 'sketchflow.v3';
export const LEGACY_STORE_KEY_V2 = 'mir-mate.studio.v2';
export const LEGACY_STORE_KEY_V1 = 'mir-mate.studio.v1';
export const ONBOARDING_KEY = 'sketchflow.onboarded.v1';

// Legacy keys for migration
export const LEGACY_ONBOARDING_KEY = 'mir-mate.onboarded.v1';
